import type { Seat } from "@ravalmatic/bridge-engine/core";
import { Button, NineSliceContainer } from "@ravalmatic/catafractum-utils";
import { GameManager } from "../../../GameManager";
import { GameVars } from "../../../GameVars";
import { GameType, type PartnershipMatchState, type MatchPartnership, type RubberGameResult, type MatchHandResult } from "../../../Types";
import { GameOverSummary } from "./GameOverSummary";
import { GameOverAvatar } from "./GameOverAvatar";
import { GameOverScore } from "./GameOverScore";

export type GameOverPartnership = MatchPartnership;

export type GameOverPlayer = {
    readonly name: string;
    readonly avatarId: number;
};

export type GameOverHandResult = MatchHandResult;

export type GameOverGameResult = RubberGameResult;

type GameOverConfigBase = {
    readonly scores: Readonly<Record<GameOverPartnership,
        Readonly<Pick<PartnershipMatchState, "totalPoints" | "gamesWon">>>>;
    readonly players: Readonly<Record<Seat, GameOverPlayer>>;
    readonly onMainMenu?: () => void;
    readonly onNewMatch?: () => void;
};

export type GameOverChicagoConfig = GameOverConfigBase & {
    readonly winningPartnership: GameOverPartnership | null;
    readonly gameType: GameType.CHICAGO;
    readonly hands: readonly GameOverHandResult[];
};

export type GameOverRubberConfig = GameOverConfigBase & {
    readonly winningPartnership: GameOverPartnership;
    readonly gameType: GameType.RUBBER;
    readonly games: readonly GameOverGameResult[];
};

export type GameOverMenuConfig = GameOverChicagoConfig | GameOverRubberConfig;

export class GameOverMenu extends Phaser.GameObjects.Container {

    private readonly background: NineSliceContainer;
    private readonly title: Phaser.GameObjects.Image;
    private readonly winnerBadge: Phaser.GameObjects.Image;
    private readonly summary: GameOverSummary;
    private readonly mainMenuButton: Button;
    private readonly newMatchButton: Button;

    constructor(scene: Phaser.Scene, public readonly config: GameOverMenuConfig) {

        super(scene);

        const frame = scene.textures.getFrame("texture_atlas_1", "bg_game_over");
        if (!frame) {
            throw new Error("Missing atlas frame: bg_game_over");
        }
        const rubber = config.gameType === GameType.RUBBER;

        this.background = new NineSliceContainer(this.scene, 0, 0, "texture_atlas_1", "bg_game_over", 950, 850, 60);
        this.background.setSize(950, 850);
        this.add(this.background);
        this.title = new Phaser.GameObjects.Image(this.scene, 0, -this.background.height / 2 - 5, "texture_atlas_1", `header_${config.gameType}`);
        this.add(this.title);
        this.winnerBadge = new Phaser.GameObjects.Image(this.scene, 0, -this.background.height * .26, "texture_atlas_1", "winner_badge"
        );
        this.winnerBadge.setScale(this.background.width * .4 / this.winnerBadge.width);
        this.add(this.winnerBadge);
        const matchOver = new Phaser.GameObjects.Text(
            scene, 0, -this.background.height * .42 + 8, "MATCH OVER", {
                fontFamily: "Baloo2-Bold", fontSize: "50px", color: "#A6E6FF",
                stroke: "#002344", strokeThickness: 2
            }
        ).setOrigin(.5).setShadow(0, 3, "#00152E", 2, false, true);
        this.add(matchOver);
        this.summary = new GameOverSummary(scene, config);
        this.summary.setY(frame.height * .01 + (rubber ? 20 : 0));
        this.summary.setScale(Math.min(
            1, this.background.width * .91 / this.summary.width,
            frame.height * .305 / this.summary.height
        ));
        this.add(this.summary);
        this.mainMenuButton = this.createButton(
            "btn_main_menu", -this.background.width * .195,
            config.onMainMenu ?? (() => GameManager.enterSplashScene())
        );
        this.newMatchButton = this.createButton(
            "btn_new_match", this.background.width * .195,
            config.onNewMatch ?? (() => GameManager.enterBoardScene(config.gameType))
        );
        this.add([this.mainMenuButton, this.newMatchButton]);
        const northSouthWins = config.winningPartnership === "northSouth";
        const draw = config.winningPartnership === null;
        this.winnerBadge.setVisible(!draw);
        const winnerLabel = new Phaser.GameObjects.Text(
            scene, 0, draw ? this.winnerBadge.y : this.winnerBadge.y - this.winnerBadge.displayHeight * .27,
            draw ? "DRAW" : northSouthWins ? "NORTH - SOUTH" : "EAST - WEST", {
                fontFamily: "Baloo2-Bold", fontSize: draw ? "60px" : "30px", color: "#FFFFFF",
                stroke: "#003418", strokeThickness: 2
            }
        ).setOrigin(.5).setShadow(0, 2, "#003418", 2, false, true);
        this.add(winnerLabel);
        const winningSeats: readonly Seat[] = northSouthWins ? [0, 2] : [1, 3];
        winningSeats.forEach((seat, index) => {
            const avatar = new GameOverAvatar(scene, seat, config.players[seat], index === 1);
            avatar.setVisible(!draw);
            avatar.setPosition(
                (index === 0 ? -1 : 1) * this.background.width * .325,
                -this.background.height * .285
            );
            this.add(avatar);
        });
        const score = new GameOverScore(scene, config);
        score.setY(-this.background.height * .06);
        this.add(score);
        this.setDepth(2000);
        this.relayout();
    }

    public relayout(): void {

        this.setPosition(GameVars.gameWidth / 2, GameVars.gameHeight / 2 + 40);
        const scale = Math.min(
            1,
            (GameVars.gameWidth - 32) / Math.max(this.background.width, this.title.width),
            (this.y - 16) / ((-this.title.y + this.title.height / 2) * GameVars.scaleY),
            (GameVars.gameHeight - this.y - 16) / (this.background.height / 2 * GameVars.scaleY)
        );
        this.setScale(scale, scale * GameVars.scaleY);
    }

    private createButton(frame: string, x: number, action: () => void): Button {

        const button = new Button(this.scene, x, this.background.height * .4, "texture_atlas_1", frame);
        const scale = this.background.width * .35 / button.width;
        button.setScale(scale);
        button.setInflationTween(1.05, scale, scale);
        button.onUp(() => {
            if (!this.visible || !button.visible || !button.input?.enabled) {
                return;
            }
            this.mainMenuButton.disable();
            this.newMatchButton.disable();
            action();
        }, this);
        return button;
    }
}
